import { Component, computed, input, InputSignal, Signal, Type } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';

import { LandingSection, LandingSectionGroup } from '@core/cms/landing.cms';
import { cmsComponentFor } from '@shared/cms/cms-section-registry';
import { CmsStyles } from '@shared/directives/cms-styles/cms-styles.directive';
import { Reveal } from '@shared/directives/reveal/reveal.directive';

/** One section resolved for rendering: its component, wrapper class, scoped styles and outlet inputs. */
interface CmsSectionView {
    readonly component: Type<unknown>;
    readonly className: string;
    readonly styles: unknown;
    readonly inputs: Record<string, unknown>;
}

/** A `blockId` group resolved for rendering: its (band) wrapper class and rendered sections. */
interface CmsGroupView {
    readonly className: string;
    readonly sections: ReadonlyArray<CmsSectionView>;
}

/**
 * Dynamic renderer for CMS landing sections. Takes the `blockId`-grouped sections (see
 * `groupSectionsByBlockId`) and renders each one via {@link NgComponentOutlet}, picking the
 * component from the registry by `__typename`. Consecutive same-`blockId` sections share a group
 * wrapper; each section's CMS `styles` JSON is injected as a scoped `<style>` and its
 * `blockClassName` applied to the wrapper.
 *
 * The grouped input is resolved into a ready-to-render view model once per change (see
 * {@link groupViews}) rather than recomputed by template bindings on every change-detection pass.
 */
@Component({
    selector: 'cms-sections',
    imports: [NgComponentOutlet, Reveal, CmsStyles],
    templateUrl: './cms-sections.html',
    styleUrl: './cms-sections.scss',
})
export class CmsSections {
    public readonly groups: InputSignal<ReadonlyArray<LandingSectionGroup>> =
        input.required<ReadonlyArray<LandingSectionGroup>>();

    /** Grouped sections mapped to render-ready view models (component, classes, styles, inputs). */
    protected readonly groupViews: Signal<ReadonlyArray<CmsGroupView>> = computed(
        (): ReadonlyArray<CmsGroupView> =>
            this.groups().map(
                (group: LandingSectionGroup): CmsGroupView => ({
                    className: this.groupClass(group),
                    sections: group.map(
                        (section: LandingSection): CmsSectionView => this.sectionView(section),
                    ),
                }),
            ),
    );

    private sectionView(section: LandingSection): CmsSectionView {
        const className: string = 'blockClassName' in section ? (section.blockClassName ?? '') : '';

        return {
            component: cmsComponentFor(section.__typename),
            className: `cms-group__section ${className}`.trim(),
            styles: 'styles' in section ? section.styles : null,
            inputs: { section },
        };
    }

    /**
     * Classes for a group wrapper. A group's `blockId` doubles as a band marker (the CMS authors it
     * as the `home__band--*` class), so applying it reuses the same coloured-band treatment as the
     * static home page — mirroring its `[class.home__band--*]` bindings.
     */
    private groupClass(group: LandingSectionGroup): string {
        const first: LandingSection | undefined = group[0];
        const band: string = first && 'blockId' in first ? (first.blockId ?? '') : '';

        return band ? `cms-group home__band ${band}` : 'cms-group';
    }
}
